/* The layer manager's persistence, as a pure module.
 *
 * Why a plain `.ts` file with no Svelte: the migration rules below are the
 * kind of thing that only ever gets exercised once, by a device that has been
 * running an OLD build — so the rules need to live somewhere `mock:verify`
 * (plain Node, no Svelte runtime) and the unit tests can both import them.
 *
 * The shape problem, stated once:
 *
 *   The 12 Aug 2026 overlay audit (e9c53a5) flipped the DEFAULTS of three
 *   layers — weather zones, tidal currents, VESSEL-ECHO — to ON. But the
 *   manager only applies a module's default to a layer whose key is MISSING
 *   from persisted state. A device that ran the pre-audit build has persisted
 *   state in which those three keys EXIST and are OFF, so the new defaults
 *   never reached it — which is exactly why the owner saw no currents on the
 *   boat. That device must come up with the new overlays ON.
 *
 *   But a user who turned something OFF under the NEW build must keep their
 *   choice. The only way to tell the two apart is a version marker: the new
 *   build writes a versioned payload, so an unversioned (legacy) payload can
 *   only have come from before this fix and gets the audit migration; a
 *   versioned payload is a user's own state and is taken as-is.
 *
 *   The old state is NOT deleted outright — that would throw away a user's
 *   deliberate choices on every other layer. Legacy values are preserved
 *   except for the audited layers, which come up ON. And the payload stays on
 *   the same `gd.layers` key, so the very next save overwrites the stale blob.
 */

/** Persisted-layer state for one layer id. */
export interface LayerState {
  visible: boolean;
  opacity: number;
}

export type LayerStateMap = Record<string, LayerState>;

/** The localStorage key, unchanged from the pre-migration manager so a legacy
 *  blob sitting under it is found and migrated rather than orphaned. */
export const LAYER_STATE_KEY = 'gd.layers';

/** The current payload shape. An UNVERSIONED payload is pre-audit legacy; a
 *  version we know but that is older gets a one-way migration; a version newer
 *  than this is a shape we cannot read (discarded → fresh defaults). */
export const LAYER_STATE_VERSION = 4;

/** The layers the 12 Aug 2026 overlay audit flipped to ON by default. A LEGACY
 *  payload was written by a pre-audit build and holds them OFF; the migration
 *  forces them visible. Keyed so a custom opacity the user set is preserved
 *  while the visibility itself comes back on.
 *
 *  VESSEL-ECHO was in this list for one day and is deliberately not any more —
 *  see FORCED_OFF_V3. A legacy device must not have it forced on either. */
export const AUDIT_DEFAULT_ON: readonly string[] = ['wxzones', 'currents'];

/** The v2 → v3 migration: layers whose visibility is forced OFF once.
 *
 *  ═══ WHY THIS NEEDS A VERSION BUMP AND NOT JUST A NEW DEFAULT ═══
 *
 *  Exactly the trap this file's header describes, running the other way. The
 *  12 Aug audit turned VESSEL-ECHO on; the owner asked for it off on 13 Aug.
 *  Flipping `defaultVisible` in the module does nothing for a device that has
 *  already run the 12 Aug build, because `ensureLayerState` only applies a
 *  default to a key that is MISSING — and those devices (every iPad on the
 *  boat: the build shipped yesterday) hold a v2 payload in which the key exists
 *  and is `true`. Without this, the layer would come back on at the helm and
 *  the change would look like it had not deployed.
 *
 *  A one-shot force is honest here because "on" was never a choice anyone made
 *  on those devices — the audit made it for them. The OPACITY the user set is
 *  preserved, and the very next toggle writes a v3 payload that is taken as-is,
 *  so turning the layer back on sticks. */
export const FORCED_OFF_V3: readonly string[] = ['vessel-echo'];

/** The v3 → v4 migration: the NOAA chart goes off, once.
 *
 *  Same mechanism, same reason, a different layer. `defaultVisible: false` has
 *  been right in `packs/base/pack.ts` all along and a fresh browser has always
 *  come up with only the Canadian chart — but `ensureLayerState` applies a
 *  default only to a key that is MISSING, so any device that ever switched
 *  NOAA on, or ran a build where it defaulted on, holds `true` and keeps it
 *  through every deploy. That is what "set the default to Canada only" has to
 *  mean in practice: not the default, the SAVED STATE.
 *
 *  Cruising ground is the Salish Sea, where the two charts overlap along the
 *  border and drawing both puts two renderings of the same water on top of
 *  each other. The user's opacity is kept, and the next toggle writes a v4
 *  payload taken as-is, so anyone who wants it back keeps it. */
export const FORCED_OFF_V4: readonly string[] = ['noaa-enc'];

/** Turn whatever came out of JSON.parse into a shape the manager trusts.
 *  Non-object entries are dropped rather than guessed at — a malformed value
 *  is a schema change we should not paper over. */
function sanitize(m: Record<string, unknown>): LayerStateMap {
  const out: LayerStateMap = {};
  for (const [k, v] of Object.entries(m)) {
    if (typeof v !== 'object' || v === null) continue;
    const s = v as { visible?: unknown; opacity?: unknown };
    out[k] = {
      visible: typeof s.visible === 'boolean' ? s.visible : false,
      opacity: typeof s.opacity === 'number' ? s.opacity : 1
    };
  }
  return out;
}

/** The whole load-time migration, pure.
 *
 *  raw        is the raw localStorage value (null when absent).
 *
 *  Returns the state map to seed the manager with. Missing keys are NOT
 *  defaulted here — `ensureLayerState` does that per layer as modules
 *  register, so a layer added tomorrow with a new default needs no migration
 *  edit.
 */
export function migrateLayerState(raw: string | null): LayerStateMap {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

  /* Versioned payload — written by this build or an earlier versioned one. */
  if ('v' in parsed) {
    const v = (parsed as { v?: unknown }).v;
    if (v === LAYER_STATE_VERSION || v === 3 || v === 2) {
      const state = (parsed as { state?: unknown }).state;
      if (state && typeof state === 'object' && !Array.isArray(state)) {
        const out = sanitize(state as Record<string, unknown>);
        /* ═══ MIGRATIONS ARE CUMULATIVE, OLDEST FIRST ═══
         *
         * A v2 device has missed BOTH forced-off rounds, so it must run v3's
         * and then v4's. Writing this as a chain rather than a switch is what
         * keeps that true when a v5 is added: the only edit is one more block
         * with one more version floor. Only keys already PRESENT are touched —
         * a device that never had the layer takes the module default like any
         * other, which is why a fresh browser needs no migration at all. */
        const force = (ids: readonly string[]) => {
          for (const id of ids) {
            if (out[id]) out[id] = { visible: false, opacity: out[id]!.opacity };
          }
        };
        if (v <= 2) force(FORCED_OFF_V3);
        if (v <= 3) force(FORCED_OFF_V4);
        return out;
      }
    }
    /* A newer version is a shape we do not understand — starting from fresh
     * defaults beats guessing at fields a future build may have renamed. */
    return {};
  }

  /* Legacy payload — the unversioned object a pre-migration build wrote.
   * Keep everything the user chose, except the audited layers come up ON. */
  const out = sanitize(parsed as Record<string, unknown>);
  for (const id of AUDIT_DEFAULT_ON) {
    out[id] = { visible: true, opacity: out[id]?.opacity ?? 1 };
  }
  return out;
}

/** A layer with no persisted entry takes its module's default. This is the
 *  "missing key → default" contract, and the reason a brand-new layer with a
 *  default of ON shows up on without anyone having to migrate state. */
export function ensureLayerState(
  state: LayerStateMap,
  id: string,
  module: { defaultVisible?: boolean; defaultOpacity?: number }
): LayerStateMap {
  if (state[id]) return state;
  return { ...state, [id]: { visible: module.defaultVisible ?? false, opacity: module.defaultOpacity ?? 1 } };
}

/** The payload `#save` writes: versioned, so the next migration run can tell
 *  this build's state from a legacy blob on the same key. */
export function serializeLayerState(state: LayerStateMap): string {
  return JSON.stringify({ v: LAYER_STATE_VERSION, state });
}
